import { useEffect, useRef } from "react";
import {
  clampLauncherOffset,
  type LauncherOffset,
  resolveLauncherOffset,
} from "@/lib/launcher-offset";
import type { WidgetEnv } from "@/lib/use-widget-env";

/**
 * Launcher offset (px) from the anchored corner. In-DOM embeds (React,
 * web-component): the effective offset (host > `theme.launcherOffset` > 0) feeds
 * `--wx-off-x/y`, which the launcher, peek and panel read as extra margin.
 * Iframe: the loader owns the frame position, so the widget only REPORTS the
 * config offset (`env.onLauncherOffset`) and its own vars stay 0; the loader
 * decides precedence against `wexioSettings.offset`.
 */
export function useLauncherOffset(env: WidgetEnv, configOffset: LauncherOffset | undefined) {
  const isIframe = env.kind === "iframe";
  const effective = resolveLauncherOffset(isIframe ? undefined : env.offset, configOffset);
  const { x, y } = isIframe ? { x: 0, y: 0 } : effective;
  const themeRoot = env.themeRoot;
  useEffect(() => {
    const root = themeRoot ?? document.documentElement;
    root.style.setProperty("--wx-off-x", `${x}px`);
    root.style.setProperty("--wx-off-y", `${y}px`);
  }, [x, y, themeRoot]);

  // Ref pattern (as `onLauncherPosition`): keeps the env out of the deps.
  const reportRef = useRef(env.onLauncherOffset);
  reportRef.current = env.onLauncherOffset;
  const clamped = clampLauncherOffset(configOffset);
  const cx = clamped?.x;
  const cy = clamped?.y;
  useEffect(() => {
    if (!isIframe || cx === undefined || cy === undefined) return;
    reportRef.current?.({ x: cx, y: cy });
  }, [isIframe, cx, cy]);
}
